import React, { useEffect, useState } from "react";

function BorrowingCapacityStep({ crmData = {}, updateCrmData }) {
  const [capacity, setCapacity] = useState(null);
  const [breakdown, setBreakdown] = useState({});

  // ---- Helper: Convert income to annual ----
  const annualiseIncome = (amount, frequency) => {
    const val = parseFloat(amount) || 0;
    switch (frequency) {
      case "Weekly":
        return val * 52;
      case "Fortnightly":
        return val * 26;
      case "Monthly":
        return val * 12;
      default:
        return val; // already annual
    }
  };

  // ---- HECS/HELP repayment 2025–26 thresholds ----
  const calculateHecsRepayment = (repaymentIncome) => {
    if (repaymentIncome <= 67000) return 0;
    if (repaymentIncome <= 125000) return (repaymentIncome - 67000) * 0.15;
    if (repaymentIncome <= 179285) return 8700 + (repaymentIncome - 125000) * 0.17;
    return repaymentIncome * 0.1;
  };

  // ---- Calculate total income ----
  const calculateTotalIncome = () => {
    const items = Array.isArray(crmData.income) ? crmData.income : [];
    return items.reduce(
      (sum, i) => sum + annualiseIncome(i.amount, i.frequency),
      0
    );
  };

  // ---- Calculate liabilities breakdown ----
  const calculateLiabilitiesBreakdown = (repaymentIncome) => {
    const liabilities =
      crmData.assetsLiabilities && Array.isArray(crmData.assetsLiabilities.liabilities)
        ? crmData.assetsLiabilities.liabilities
        : [];

    let breakdown = {
      creditCards: 0,
      hecs: 0,
      loans: 0,
    };

    liabilities.forEach((l) => {
      const type = l.type || "";
      const limit = parseFloat(l.limit) || 0;
      const repayment = parseFloat(l.repayment) || 0;

      if (type === "Credit Card") {
        breakdown.creditCards += limit * 0.03 * 12; // annualise
      } else if (type === "HECS/HELP") {
        breakdown.hecs += calculateHecsRepayment(repaymentIncome);
      } else {
        breakdown.loans += repayment * 12; // assume monthly repayments
      }
    });

    breakdown.total = breakdown.creditCards + breakdown.hecs + breakdown.loans;
    return breakdown;
  };

  // ---- Main borrowing capacity ----
  const calculateCapacity = () => {
    const totalIncome = calculateTotalIncome();
    const liabilitiesBreakdown = calculateLiabilitiesBreakdown(totalIncome);

    const netServicing = totalIncome * 0.3 - liabilitiesBreakdown.total;

    const repaymentFactor = 0.00632; // ~6.5% interest, 30 years
    const capacityEstimate = netServicing > 0 ? netServicing / repaymentFactor : 0;

    setBreakdown({
      income: totalIncome,
      liabilities: liabilitiesBreakdown,
      netServicing,
    });

    return Math.max(capacityEstimate, 0);
  };

  useEffect(() => {
    const result = calculateCapacity();
    setCapacity(result);

    if (updateCrmData) {
      updateCrmData({ borrowingCapacity: result });
    }
  }, [crmData]);

  return (
    <div>
      <h2>Borrowing Capacity</h2>

      {capacity !== null ? (
        <>
          <div style={{ fontSize: "1.4rem", fontWeight: "bold", marginTop: "1rem" }}>
            Estimated Capacity: ${capacity.toLocaleString()}
          </div>

          {/* Breakdown Table */}
          <table style={{ marginTop: "1rem", borderCollapse: "collapse", width: "100%" }}>
            <thead>
              <tr style={{ background: "#f0f0f0" }}>
                <th style={{ textAlign: "left", padding: "8px" }}>Category</th>
                <th style={{ textAlign: "right", padding: "8px" }}>Annual Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ padding: "8px" }}>Total Income</td>
                <td style={{ textAlign: "right", padding: "8px" }}>
                  ${breakdown.income?.toLocaleString()}
                </td>
              </tr>
              <tr>
                <td style={{ padding: "8px" }}>Credit Cards</td>
                <td style={{ textAlign: "right", padding: "8px" }}>
                  ${breakdown.liabilities?.creditCards?.toLocaleString()}
                </td>
              </tr>
              <tr>
                <td style={{ padding: "8px" }}>HECS/HELP</td>
                <td style={{ textAlign: "right", padding: "8px" }}>
                  ${breakdown.liabilities?.hecs?.toLocaleString()}
                </td>
              </tr>
              <tr>
                <td style={{ padding: "8px" }}>Loans (Personal, Car, Home)</td>
                <td style={{ textAlign: "right", padding: "8px" }}>
                  ${breakdown.liabilities?.loans?.toLocaleString()}
                </td>
              </tr>
              <tr style={{ fontWeight: "bold" }}>
                <td style={{ padding: "8px" }}>Total Liabilities</td>
                <td style={{ textAlign: "right", padding: "8px" }}>
                  ${breakdown.liabilities?.total?.toLocaleString()}
                </td>
              </tr>
              <tr>
                <td style={{ padding: "8px" }}>Net Servicing (30% buffer)</td>
                <td style={{ textAlign: "right", padding: "8px" }}>
                  ${breakdown.netServicing?.toLocaleString()}
                </td>
              </tr>
            </tbody>
          </table>
        </>
      ) : (
        <p>Please complete income and liabilities to view capacity.</p>
      )}

      <button
        onClick={() => setCapacity(calculateCapacity())}
        style={{ marginTop: "1rem", background: "#4EDCF6", padding: "8px 16px" }}
      >
        🔄 Recalculate
      </button>
    </div>
  );
}

export default BorrowingCapacityStep;
